import React, { useState, useEffect, startTransition } from 'react';
import {
  Box,
  TextField,
  Button,
  Paper,
  Divider,
  CircularProgress,
  Tooltip,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { IFormData, IDocumentInput } from '@homeporter/types';
import { useFormStyles } from './styles';

const reindex = (data: IFormData[]) => {
  let idx = 0;
  data.forEach((sectionEntry: any) => {
    sectionEntry.children.forEach((child: any) => {
      child.items.forEach((item: any) => {
        item.idx = idx++;
      });
    });
  });
};

export const FormEditor: React.FC<{
  formDataStr: string;
  setFormDataStr: (formDataStr: string) => void;
  documentLoading: boolean;
}> = ({ formDataStr, documentLoading, setFormDataStr }) => {
  const styles = useFormStyles();
  const [formData, setFormData] = useState<IFormData[] | null>(null);

  useEffect(() => {
    if (!formDataStr) return;
    setFormData(JSON.parse(formDataStr));
  }, [formDataStr]);

  const addDocument = () => {
    if (!formData) return;
    setFormData([
      ...formData,
      {
        title: '',
        description: '',
        children: [
          {
            subtitle: '',
            description: '',
            items: [createNewItem()],
          },
        ],
      },
    ]);
  };

  const createNewItem = (): IDocumentInput => ({
    idx: 0,
    label: '',
    criteria: '',
    type: '',
  });

  const updateDocument = (
    index: number,
    field: 'title' | 'description', //keyof IFormData,
    value: string,
  ) => {
    if (!formData) return;
    const updated = [...formData];
    updated[index][field] = value;
    setFormData(updated);
    setFormDataStr(JSON.stringify(updated, null, 2));
  };

  const updateChild = (
    docIndex: number,
    childIndex: number,
    field: keyof IFormData['children'][0],
    value: string,
  ) => {
    if (!formData) return;
    const updated = [...formData];
    (updated[docIndex].children[childIndex] as any)[field] = value;
    setFormData(updated);
    setFormDataStr(JSON.stringify(updated, null, 2));
  };

  const updateItem = (
    docIndex: number,
    childIndex: number,
    itemIndex: number,
    field: keyof IDocumentInput,
    value: any,
  ) => {
    if (!formData) return;
    const updated = [...formData];
    (updated[docIndex].children[childIndex].items[itemIndex] as any)[field] =
      value;
    setFormData(updated);
    setFormDataStr(JSON.stringify(updated, null, 2));
  };

  const addChild = (docIndex: number) => {
    if (!formData) return;
    const updated = [...formData];
    console.log('::::addChild updated', updated);
    if (!updated[docIndex].children) updated[docIndex].children = [];
    updated[docIndex].children.push({
      subtitle: '',
      description: '',
      items: [createNewItem()],
    });
    reindex(updated);
    setFormData(updated);
    setFormDataStr(JSON.stringify(updated, null, 2));
  };

  const addItem = (docIndex: number, childIndex: number) => {
    if (!formData) return;
    const updated = [...formData];
    updated[docIndex].children[childIndex].items.push(createNewItem());

    console.log('::::addItem updated', updated);
    reindex(updated);
    setFormData(updated);
    setFormDataStr(JSON.stringify(updated, null, 2));
  };

  const removeItem = (
    docIndex: number,
    childIndex: number,
    itemIndex: number,
  ) => {
    if (!formData) return;
    const updated = [...formData];
    updated[docIndex].children[childIndex].items.splice(itemIndex, 1);
    setFormData(updated);
    setFormDataStr(JSON.stringify(updated, null, 2));
  };
  /*
  const [formUI, setFormUI] = useState<React.ReactNode | null>(null);
  useEffect(() => {
    if (!formData) return;

    startTransition(() => {
      const ui = formData.map((doc, docIndex) => (
        <Paper key={docIndex} sx={{ p: '1rem', mb: '1rem' }}>
          <TextField
            fullWidth
            label="Title"
            value={doc.title}
            onChange={(e) => updateDocument(docIndex, 'title', e.target.value)}
            sx={{ mb: '1rem' }}
          />
          <TextField
            fullWidth
            label="Description"
            value={doc.description || ''}
            onChange={(e) =>
              updateDocument(docIndex, 'description', e.target.value)
            }
            sx={{ mb: '2rem' }}
          />

          {doc.children?.map((child, childIndex) => (
            <Box key={childIndex} sx={styles.childBox}>
              <TextField
                fullWidth
                label="Question/Subtitle *"
                value={child.subtitle}
                onChange={(e) =>
                  updateChild(docIndex, childIndex, 'subtitle', e.target.value)
                }
                sx={{ mb: '1rem' }}
              />
              <TextField
                fullWidth
                label="Description"
                value={child.description || ''}
                onChange={(e) =>
                  updateChild(
                    docIndex,
                    childIndex,
                    'description',
                    e.target.value,
                  )
                }
                sx={{ mb: '1rem' }}
              />

              {child.items.map((item, itemIndex) => (
                <Box key={item.id} sx={styles.itemBox}>
                  <TextField
                    fullWidth
                    label="Label"
                    value={item.label}
                    onChange={(e) =>
                      updateItem(
                        docIndex,
                        childIndex,
                        itemIndex,
                        'label',
                        e.target.value,
                      )
                    }
                  />
                  <TextField
                    fullWidth
                    label="AI Criteria"
                    value={item.criteria}
                    onChange={(e) =>
                      updateItem(
                        docIndex,
                        childIndex,
                        itemIndex,
                        'criteria',
                        e.target.value,
                      )
                    }
                  />
                  <Box sx={styles.typeBox}>
                    <Tooltip
                      title="Enter: 'boolean', 'string' or comma separated strings"
                      arrow
                      placement="top">
                      <TextField
                        label="Type *"
                        value={
                          Array.isArray(item.type)
                            ? item.type.join(', ')
                            : item.type
                        }
                        onChange={(e) =>
                          updateItem(
                            docIndex,
                            childIndex,
                            itemIndex,
                            'type',
                            e.target.value.includes(',')
                              ? e.target.value.split(',').map((t) => t.trim())
                              : e.target.value,
                          )
                        }
                        sx={{ flex: 1 }}
                      />
                    </Tooltip>
                    <Button
                      variant="text"
                      size="small"
                      onClick={() =>
                        removeItem(docIndex, childIndex, itemIndex)
                      }>
                      Remove
                    </Button>
                  </Box>
                </Box>
              ))}

              <Button
                variant="outlined"
                size="small"
                sx={styles.addItemButton}
                startIcon={<AddIcon />}
                onClick={() => addItem(docIndex, childIndex)}>
                Add Input Item
              </Button>
            </Box>
          ))}

          <Button
            variant="contained"
            size="small"
            onClick={() => addChild(docIndex)}
            sx={{ mt: 1 }}>
            + Add Subsection
          </Button>
        </Paper>
      ));

      setFormUI(ui);
    });
  }, [formData]);
*/
  if (!formData || documentLoading) {
    return (
      <Box sx={styles.documentLoading}>
        <CircularProgress size={'2rem'} sx={{ mr: '.5rem' }} />
      </Box>
    );
  }

  return (
    <Box sx={styles.formEditor}>
      {formData?.map((doc, docIndex) => (
        <Paper key={'doc-' + docIndex} sx={{ p: '1rem', mb: '1rem' }}>
          <TextField
            fullWidth
            label="Title"
            value={doc.title}
            onChange={(e) => updateDocument(docIndex, 'title', e.target.value)}
            sx={{ mb: '1rem' }}
          />
          <TextField
            fullWidth
            label="Description"
            value={doc.description || ''}
            onChange={(e) =>
              updateDocument(docIndex, 'description', e.target.value)
            }
            sx={{ mb: '2rem' }}
          />

          {doc.children.map((child, childIndex) => (
            <Box key={'child-' + childIndex} sx={styles.childBox}>
              <TextField
                fullWidth
                label="Question/Subtitle *"
                value={child.subtitle}
                onChange={(e) =>
                  updateChild(docIndex, childIndex, 'subtitle', e.target.value)
                }
                sx={{ mb: '1rem' }}
              />
              <TextField
                fullWidth
                label="Description"
                value={child.description || ''}
                onChange={(e) =>
                  updateChild(
                    docIndex,
                    childIndex,
                    'description',
                    e.target.value,
                  )
                }
                sx={{ mb: '1rem' }}
              />

              {child.items.map((item, itemIndex) => (
                <Box
                  key={'item-' + item.idx + '-' + itemIndex}
                  sx={styles.itemBox}>
                  <TextField
                    fullWidth
                    label="Label"
                    value={item.label}
                    onChange={(e) =>
                      updateItem(
                        docIndex,
                        childIndex,
                        itemIndex,
                        'label',
                        e.target.value,
                      )
                    }
                  />
                  <TextField
                    fullWidth
                    label="AI Criteria"
                    value={item.criteria}
                    onChange={(e) =>
                      updateItem(
                        docIndex,
                        childIndex,
                        itemIndex,
                        'criteria',
                        e.target.value,
                      )
                    }
                  />
                  <Box sx={styles.typeBox}>
                    <Tooltip
                      title="Enter: 'boolean', 'string' or comma separated strings"
                      arrow
                      placement="top">
                      <TextField
                        label="Type *"
                        value={
                          Array.isArray(item.type)
                            ? item.type.join(', ')
                            : item.type
                        }
                        onChange={(e) =>
                          updateItem(
                            docIndex,
                            childIndex,
                            itemIndex,
                            'type',
                            e.target.value.includes(',')
                              ? e.target.value.split(',').map((t) => t.trim())
                              : e.target.value,
                          )
                        }
                        sx={{ flex: 1 }}
                      />
                    </Tooltip>
                    <Button
                      variant="text"
                      size="small"
                      onClick={() =>
                        removeItem(docIndex, childIndex, itemIndex)
                      }>
                      Remove
                    </Button>
                  </Box>
                </Box>
              ))}

              <Button
                variant="outlined"
                size="small"
                sx={styles.addItemButton}
                startIcon={<AddIcon />}
                onClick={() => addItem(docIndex, childIndex)}>
                Add Input Item
              </Button>
            </Box>
          ))}

          <Button
            variant="contained"
            size="small"
            onClick={() => addChild(docIndex)}
            sx={{ mt: 1 }}>
            + Add Subsection
          </Button>
        </Paper>
      ))}

      <Divider sx={{ my: 2 }} />

      <Button
        variant="contained"
        color="primary"
        startIcon={<AddIcon />}
        onClick={addDocument}>
        Add Section
      </Button>
    </Box>
  );
};
